import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AuthService } from 'src/app/services/auth/auth.service';
import { horaTexto } from 'src/app/services/odoo/odoo-formato';
import { OdooToastService } from 'src/app/services/odoo/odoo-toast.service';
import {
  OdooBuzonTipo, OdooConfigCambio, PERMISO_ODOO_ESCRIBIR,
} from 'src/app/services/odoo/odoo.models';
import { mensajeDeFallo } from 'src/app/services/odoo/odoo.service';
import { OdooBitacoraService, OdooConfigVista, OdooPruebaVista } from 'src/app/services/odoo/odoo-bitacora.service';

type Interruptor = 'enabled' | 'crear_activo' | 'modificar_activo' | 'archivos_activo';

interface InterruptorDef { campo: Interruptor; titulo: string; ayuda: string; }

/**
 * CONFIGURACIÓN — los interruptores se guardan al tocarlos: quedan ocupados mientras guardan
 * y, si falla, vuelven a como estaban (deshacer). La clave de Odoo nunca se muestra y solo
 * se envía si se escribe una nueva.
 */
@Component({
  selector: 'app-odoo-config',
  templateUrl: './odoo-config.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooConfigComponent implements OnInit {
  cfg: OdooConfigVista | null = null;
  cargando = true;
  error: string | null = null;

  ocupados: ReadonlySet<string> = new Set<string>();
  guardandoConexion = false;
  probando = false;

  // Campos de texto (se guardan con su botón)
  url = '';
  usuario = '';
  clave = '';
  carpeta = '';

  /** Alerta cuando la clave vence en menos de estos días. */
  readonly diasAviso = 60;

  readonly interruptores: InterruptorDef[] = [
    { campo: 'enabled', titulo: 'Avisar a Odoo', ayuda: 'Interruptor general. Apagado, no se le avisa nada a Odoo.' },
    { campo: 'crear_activo', titulo: 'Anotar clientes y comercios nuevos', ayuda: 'Al aprobar un cliente o un comercio, se anota en Odoo.' },
    { campo: 'modificar_activo', titulo: 'Avisar los cambios', ayuda: 'Cuando cambian los datos de alguien ya anotado, se actualiza en Odoo.' },
    { campo: 'archivos_activo', titulo: 'Subir el archivo del día', ayuda: 'Cada día se arma y se sube el archivo de créditos y el de pagos.' },
  ];
  readonly horas = Array.from({ length: 24 }, (_, h) => ({ valor: h, texto: horaTexto(h) }));

  constructor(private odoo: OdooBitacoraService, public _auth: AuthService, private toast: OdooToastService, private cdr: ChangeDetectorRef) {}

  get puedeEditar(): boolean { return this._auth.hasPermission(PERMISO_ODOO_ESCRIBIR); }

  ngOnInit(): void { this.cargar(); }

  cargar(): void {
    this.cargando = true;
    this.error = null;
    this.odoo.config().subscribe({
      next: (c) => { this.cfg = c; this.url = c.api_url || ''; this.usuario = c.api_login || ''; this.carpeta = c.drive_folder_id || ''; this.cargando = false; this.cdr.markForCheck(); },
      error: (e: unknown) => { this.error = mensajeDeFallo(e); this.cargando = false; this.cdr.markForCheck(); },
    });
  }

  private marcar(clave: string, ocupado: boolean): void {
    const n = new Set(this.ocupados);
    if (ocupado) n.add(clave); else n.delete(clave);
    this.ocupados = n;
  }

  /** Guarda un cambio ya: lo muestra al instante y lo deshace si falla. */
  aplicar(clave: string, cambio: OdooConfigCambio, aviso: string): void {
    if (!this.cfg || !this.puedeEditar || this.ocupados.has(clave)) return;
    const previo = this.cfg;
    this.cfg = { ...previo, ...cambio, tiene_clave: previo.tiene_clave } as OdooConfigVista;
    this.marcar(clave, true);
    this.odoo.guardarConfig(cambio).subscribe({
      next: (c) => { this.cfg = { ...previo, ...c, conexion: previo.conexion }; this.marcar(clave, false); this.toast.mostrar(aviso); this.cdr.markForCheck(); },
      error: (e: unknown) => {
        this.cfg = previo;
        this.marcar(clave, false);
        this.toast.mostrar(`No se guardó, quedó como estaba. ${mensajeDeFallo(e)}`, false);
        this.cdr.markForCheck();
      },
    });
  }

  alternar(def: InterruptorDef, valor: boolean): void {
    const cambio: OdooConfigCambio = { [def.campo]: valor };
    this.aplicar(def.campo, cambio, `${def.titulo}: ${valor ? 'activado' : 'apagado'}.`);
  }

  cambiarHora(valor: string): void {
    const h = Number(valor);
    if (Number.isInteger(h) && h >= 0 && h <= 23) this.aplicar('hora_subida', { hora_subida: h }, `Hora de subida: ${horaTexto(h)}.`);
  }

  cambiarBuzon(valor: string): void {
    if (valor === 'local' || valor === 'drive') this.aplicar('buzon_tipo', { buzon_tipo: valor as OdooBuzonTipo }, 'Buzón cambiado.');
  }

  guardarCarpeta(): void { this.aplicar('drive_folder_id', { drive_folder_id: this.carpeta.trim() }, 'Carpeta guardada.'); }

  guardarConexion(): void {
    if (!this.puedeEditar || this.guardandoConexion) return;
    const cambio: OdooConfigCambio = { api_url: this.url.trim(), api_login: this.usuario.trim() };
    if (this.clave) cambio.api_password = this.clave; // la clave solo viaja si se escribió
    this.guardandoConexion = true;
    this.odoo.guardarConfig(cambio).subscribe({
      next: (c) => {
        this.cfg = { ...this.cfg, ...c, conexion: this.cfg ? this.cfg.conexion : null } as OdooConfigVista; this.clave = ''; this.guardandoConexion = false;
        this.toast.mostrar('Datos de conexión guardados.'); this.cdr.markForCheck();
      },
      error: (e: unknown) => { this.guardandoConexion = false; this.toast.mostrar(mensajeDeFallo(e), false); this.cdr.markForCheck(); },
    });
  }

  probar(): void {
    if (!this.puedeEditar || this.probando || !this.cfg) return;
    this.probando = true;
    this.odoo.probar().subscribe({
      next: (p) => this.alProbar(p),
      error: (e: unknown) => this.alProbar({ estado: 'error', mensaje: mensajeDeFallo(e), ms: 0 }),
    });
  }

  private alProbar(p: OdooPruebaVista): void {
    const ahora = new Date().toISOString();
    if (this.cfg) {
      const dias = typeof p.credencial_dias === 'number' ? p.credencial_dias : this.cfg.credencial_dias;
      this.cfg = {
        ...this.cfg,
        conexion: { ok: p.estado === 'ok', mensaje: p.mensaje, probado_en: ahora },
        credencial_dias: dias,
        credencial_revisada: typeof p.credencial_dias === 'number' ? ahora : this.cfg.credencial_revisada,
      };
    }
    this.probando = false;
    this.toast.mostrar(p.estado === 'ok' ? 'Conexión con Odoo correcta.' : p.mensaje, p.estado === 'ok');
    this.cdr.markForCheck();
  }

  /** La clave vence pronto (menos de `diasAviso` días). */
  get claveVencePronto(): boolean {
    const d = this.cfg ? this.cfg.credencial_dias : null;
    return typeof d === 'number' && d < this.diasAviso;
  }

  get textoVence(): string {
    const d = this.cfg ? this.cfg.credencial_dias : null;
    if (typeof d !== 'number') return '';
    return d <= 0 ? 'ya' : `en ${d} ${d === 1 ? 'día' : 'días'}`;
  }

  porCampo = (_: number, d: InterruptorDef): string => d.campo;
  porHora = (_: number, h: { valor: number }): number => h.valor;
}
